import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Trophy, Calendar, ChevronRight, ArrowLeft, Shield, MapPin, Crown, Medal, Award
} from 'lucide-react';
import { authAPI } from '../api';

const DD_BASE = 'https://ddragon.leagueoflegends.com/cdn/14.24.1';

function tierColor(tier) {
  const colors = {
    IRON: '#7c8594', BRONZE: '#8c5a3c', SILVER: '#8fa3a8',
    GOLD: '#d4a84b', PLATINUM: '#4fa67f', EMERALD: '#2ecc71',
    DIAMOND: '#4d8eff', MASTER: '#9b59b6', GRANDMASTER: '#e74c3c',
    CHALLENGER: '#f1c40f'
  };
  return colors[tier?.toUpperCase()] || '#4a5568';
}

function rankLabel(tier, rank) {
  if (!tier || tier === 'UNRANKED') return 'Non classé';
  return `${tier} ${rank || ''}`.trim();
}

function statusLabel(s) {
  return {
    registration: 'Inscriptions', seeding: 'Seeding',
    'in-progress': 'En cours', finished: 'Terminé'
  }[s] || s;
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

export default function TeamPage() {
  const { id } = useParams();
  const [team, setTeam] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [teamRes, tourRes] = await Promise.all([
          authAPI.getTeam(id),
          authAPI.getTeamTournaments(id)
        ]);
        setTeam(teamRes.data);
        setTournaments(tourRes.data || []);
      } catch (e) {
        setError("Équipe introuvable");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <div className="loading">Chargement...</div>;
  if (error || !team) return (
    <div className="tp-not-found">
      <Shield size={48} style={{ color: 'var(--color-accent-1)' }} />
      <h2>Équipe introuvable</h2>
      <Link to="/teams" className="tp-back-link"><ArrowLeft size={16} /> Retour aux équipes</Link>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="team-page">

      {/* ── Header ── */}
      <div className="team-page-hero">
        <div className="team-page-hero-bg" />
        <div className="team-page-hero-content">
          <Link to="/teams" className="tp-back-link">
            <ArrowLeft size={16} /> Toutes les équipes
          </Link>
          <div className="team-page-identity">
            <div className="team-page-logo-wrap">
              {team.logo
                ? <img src={team.logo} alt={team.name} className="team-page-logo"
                    onError={e => { e.target.style.display = 'none'; }} />
                : <div className="team-page-logo-placeholder"><Shield size={36} /></div>
              }
            </div>
            <div>
              <div className="team-page-name-row">
                <div className="team-page-name">{team.name}</div>
                {team.tag && <span className="team-page-tag">[{team.tag}]</span>}
                {team.ranking && (
                  <span className={`team-page-ranking${team.ranking <= 3 ? ' team-page-ranking--top' : ''}`}>
                    {team.ranking === 1 ? <Crown size={14} /> : team.ranking <= 3 ? <Medal size={14} /> : '#'}
                    {team.ranking === 1 ? ' 1er' : team.ranking === 2 ? ' 2e' : team.ranking === 3 ? ' 3e' : team.ranking + 'e'}
                  </span>
                )}
              </div>

              {/* Badges tournois gagnés */}
              {team.tournamentsWon?.length > 0 && (
                <div className="team-page-badges">
                  {team.tournamentsWon.map(tw => (
                    <Link key={tw.id} to={`/tournament/${tw.id}`} className="team-page-badge" title={tw.name}>
                      <Trophy size={12} />
                      <span>{tw.name}</span>
                    </Link>
                  ))}
                </div>
              )}

              <div className="team-page-meta">
                <span><Users size={13} /> {team.members?.length || 0} membre(s)</span>
                {team.createdAt && (
                  <span>
                    <Calendar size={13} /> Créée le {formatDate(team.createdAt)}
                    <span className="team-page-meta-pts"><Trophy size={11} /> {team.points || 0} pts</span>
                  </span>
                )}
                {team.owner && <span><Shield size={13} /> {team.owner.displayName || team.owner.username}</span>}
              </div>
            </div>
          </div>

          {/* ── Stats / Points ── */}
          <div className="team-page-stats-bar">
            <div className="team-page-stat team-page-stat--pts">
              <Trophy size={20} />
              <span className="team-page-stat-value">{team.points || 0}</span>
              <span className="team-page-stat-label">points</span>
            </div>
            <div className="team-page-stat team-page-stat--wins">
              <span className="team-page-stat-value">{team.wins || 0}</span>
              <span className="team-page-stat-label">victoires</span>
            </div>
            <div className="team-page-stat team-page-stat--losses">
              <span className="team-page-stat-value">{team.losses || 0}</span>
              <span className="team-page-stat-label">défaites</span>
            </div>
          </div>
        </div>
      </div>

      <div className="team-page-body">

        {/* ── Membres ── */}
        <section className="team-page-section">
          <h2 className="team-page-section-title"><Users size={18} /> Membres</h2>
          <div className="team-page-members-list">
            {team.members?.length > 0 ? team.members.map((m, i) => {
              const isOwner = m.user?.id === team.owner?.id || m.user?._id === team.owner?._id;
              const iconUrl = m.user?.profileIconId
                ? `${DD_BASE}/img/profileicon/${m.user.profileIconId}.png`
                : null;
              const rankColor = tierColor(m.user?.tier);
              return (
                <motion.div key={m._id || i} className="team-page-member-row"
                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}>

                  {/* Icône invocateur */}
                  <div className="team-page-member-icon-wrap">
                    {iconUrl ? (
                      <img src={iconUrl} alt="icon" className="team-page-member-icon"
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="team-page-member-icon-placeholder">
                        {((m.user?.riotId || m.user?.displayName || m.user?.username || '?')[0]).toUpperCase()}
                      </div>
                    )}
                    {m.user?.level && (
                      <span className="team-page-member-level">{m.user.level}</span>
                    )}
                  </div>

                  {/* Infos */}
                  <div className="team-page-member-info">
                    <div className="team-page-member-name-row">
                      <span className="team-page-member-name">
                        {m.user?.riotId || m.user?.displayName || m.user?.username || 'Inconnu'}
                      </span>
                      {m.user?.tagLine && (
                        <span className="team-page-member-tag">#{m.user.tagLine}</span>
                      )}
                      {isOwner && (
                        <span className="team-page-owner-badge">Capitaine</span>
                      )}
                    </div>
                    <div className="team-page-member-ranks">
                      <div className="team-page-member-rank-item">
                        <span className="team-page-rank-queue">Solo/Duo</span>
                        <div className="team-page-member-rank"
                          style={{ color: rankColor, borderColor: rankColor }}>
                          <Trophy size={10} />
                          {rankLabel(m.user?.tier, m.user?.rank)}
                        </div>
                      </div>
                      <div className="team-page-member-rank-item">
                        <span className="team-page-rank-queue">Flex</span>
                        <div className="team-page-member-rank"
                          style={{ color: tierColor(m.user?.flexTier), borderColor: tierColor(m.user?.flexTier) }}>
                          <Trophy size={10} />
                          {rankLabel(m.user?.flexTier, m.user?.flexRank)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {m.joinedAt && (
                    <span className="team-page-member-date">
                      depuis {formatDate(m.joinedAt)}
                    </span>
                  )}
                </motion.div>
              );
            }) : (
              <p className="team-page-empty">Aucun membre</p>
            )}
          </div>
        </section>

        {/* ── Historique tournois ── */}
        <section className="team-page-section">
          <h2 className="team-page-section-title"><Trophy size={18} /> Historique des tournois</h2>
          {tournaments.length === 0 ? (
            <p className="team-page-empty">Cette équipe n'a pas encore participé à des tournois.</p>
          ) : (
            <div className="team-page-tournaments-list">
              {tournaments.map((t, i) => (
                <motion.div key={t._id} className="team-page-tournament-row"
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}>
                  <div className="team-page-t-logo">
                    {t.logo
                      ? <img src={t.logo} alt={t.name} onError={e => { e.target.style.display='none'; }} />
                      : <Trophy size={18} />
                    }
                  </div>
                  <div className="team-page-t-info">
                    <span className="team-page-t-name">{t.name}</span>
                    <span className="team-page-t-format">{t.format}</span>
                  </div>
                  <span className={`team-page-t-status team-page-t-status--${t.status}`}>
                    {statusLabel(t.status)}
                  </span>
                  {t.startDate && (
                    <span className="team-page-t-date">
                      <MapPin size={12} /> {formatDate(t.startDate)}
                    </span>
                  )}
                  <Link to={`/tournament/${t._id}`} className="team-page-t-link">
                    Voir <ChevronRight size={14} />
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </section>

      </div>
    </motion.div>
  );
}
